<!-- eslint-disable complexity -->
<script lang="ts" setup>
import { Preferences } from '@3ds/common-preferences'
import { DS_LANG } from '@ds/swymkit/config'
import { SERVICE, logout, requirejsOne } from '@ds/swymkit/tools'
import type { LegalOptions, PlatformAPI_AMD, W3DLegalItem } from '@ds/swymkit/typings'
import { vElementHover } from '@vueuse/components'
import type { DropdownMenu } from 'vuekit'

import { ROUTE_ADMIN } from '~/contexts/admin-tenant/router/routes'
import { ROUTE_ME } from '~/contexts/me/router/routes'
import { ROUTE_NETWORK } from '~/contexts/network/router/routes'
import { ROUTE_NOTIFICATION } from '~/contexts/notification/router/routes'

import { useCommunitiesStore } from '~/entities/container/community/stores/communities'
import { useConversationsStore } from '~/entities/container/conversation/stores/conversations'
import { useUsersStore } from '~/entities/user/user/stores/users'

import { AI_NAME } from '~/features-swym/aura/config/ai'
import {
  useServiceWorker,
  useServiceWorkerWrapper,
} from '~/features-swym/native-progressive-web-app/composables/service-worker'
import { useInvitationsStore } from '~/features-swym/network-invitation/stores/network-invitations'

import { EContext } from '~/features-shared/config-manager/interfaces/contexts'
import { notificationCount } from '~/features-shared/left-panel/stores/states'
import MBlockItem from '~/features-shared/libs/components/m-block-item.vue'
import { isLegalInfoFetched } from '~/features-shared/topframe/stores/states'
import { getLegalInfoFiles } from '~/features-shared/topframe/utils/download-legal-files'

import { getServiceUrl } from '~/features-generic/compass/utils/compass-services'
import { useFeatureManager } from '~/features-generic/config-manager/composables/feature-manager'
import { hasAccessTo } from '~/features-generic/config-manager/composables/page-manager'
import { useTracker } from '~/features-generic/tracker/composables/tracker'
import { useTranslations } from '~/features-generic/translations/composables/translations'

/**
 * @props
 */
const props = defineProps<{
  /** The current context. eg, DM, COMMUNITY, CALL, etc... */
  currentContext: EContext

  /** The context that is opened: eg, current context is DM but user opened COMMUNITY panel */
  openContext: EContext

  /**
   * What layout shoud be use?
   * - left: "desktop mode" : items will be displayed with border on left side
   * - bottom: "mobile mode" : items will be displayed with border on top side
   */
  layout: 'left' | 'bottom'

  /**
   * Set true to display less items (5 max) in the navbar
   * @default false
   */
  compact?: boolean
}>()

/**
 * @emits
 */
const emit = defineEmits<{
  (e: 'update:context-active', value: EContext | undefined): void
  (e: 'update:context-pin', value: EContext | undefined): void
}>()

const router = useRouter()
const isWidget = !!window.widget?.id
const isLogoutActivated = useFeatureManager('topbar.logout')
const isMobileBreakpoint = useFeatureManager('app.config.mobile_breakpoint')
const isRitualsActivated = useFeatureManager('rituals.enabled')
const isAURAEnabled = useFeatureManager('ai.aura.enable')
const hasCalls = useFeatureManager('calls')
const userStore = useUsersStore()
const storeCommunities = useCommunitiesStore()
const storeConversations = useConversationsStore()
const storeInvitations = useInvitationsStore()
const storeServiceWorker = useServiceWorker()
const { $i18n } = useTranslations('navigation-bar')
const { trackPageEvent } = useTracker('navigation-bar')
const [showMore, toggle] = useToggle()

interface IMenuItem {
  badge?: number
  name: string
  icon: string
  active: boolean
  open: boolean
  context?: EContext
  type?: string
  nativeContext?: any
}

// HTP1: add custom menu items for app native
const appProps = Preferences.getAppProperties()
const W3DLegalItems = ref<DropdownMenu.Item[]>([])
const addonMobileItems = computed(() => {
  const nativeItems: { text: string, fonticon: string, handler: () => void }[] = []
  if (Preferences.isNativeApp()) {
    if (appProps && appProps.navBarItems) {
      appProps.navBarItems.forEach((item: any) => {
        nativeItems.push({
          text: $i18n(item.text),
          fonticon: item.fonticon,
          handler: async () => {
            if (item.type === 'native') {
              const PlatformAPI = await requirejsOne<PlatformAPI_AMD>('DS/PlatformAPI/PlatformAPI')
              PlatformAPI.publish(item.handlerRoute, item.handlerRouteData)
            }
            else {
              router.push(item.handlerRoute)
            }
          },
        })
      })
    }
  }
  return nativeItems
})

const MENUS_APPUPDATE = computed<IMenuItem>(() => ({
  name: $i18n('AppUpdate'),
  icon: 'reload',
  context: EContext.APPUPDATE,
  active: false,
  open: false,
  type: 'no-navigation',
}))
const MENUS_AURA = computed<IMenuItem>(() => ({
  name: AI_NAME,
  icon: 'virtual-companions',
  context: EContext.AURA,
  active: props.currentContext === EContext.AURA,
  open: props.openContext === EContext.AURA,
}))
const MENUS_COMMUNITIES = computed<IMenuItem>(() => ({
  badge: storeCommunities.countUnread,
  name: $i18n('Communities'),
  icon: 'logo-3dswym',
  context: EContext.COMMUNITY,
  active: props.currentContext === EContext.COMMUNITY,
  open: props.openContext === EContext.COMMUNITY,
}))
const MENUS_CONVERSATIONS = computed<IMenuItem>(() => ({
  badge: storeConversations.countUnread,
  name: $i18n('Conversations'),
  icon: 'chat-alt',
  context: EContext.DM,
  active: props.currentContext === EContext.DM,
  open: props.openContext === EContext.DM,
}))
const MENUS_RITUALS = computed<IMenuItem>(() => ({
  name: $i18n('Rituals'),
  icon: 'calendar-clock',
  context: EContext.RITUALS,
  active: props.currentContext === EContext.RITUALS,
  open: props.openContext === EContext.RITUALS,
}))
const MENUS_CALLS = computed<IMenuItem>(() => ({
  badge: storeConversations.countMissedCalls,
  name: $i18n('Calls'),
  icon: 'phone',
  context: EContext.CALLS,
  active: props.currentContext === EContext.CALLS,
  open: props.openContext === EContext.CALLS,
}))
const MENUS_MORE = computed<IMenuItem>(() => ({
  name: $i18n('More'),
  icon: 'menu-dot',
  active: false,
  open: false,
}))
const MENUS_NOTIFICATIONS = computed<IMenuItem>(() => ({
  name: $i18n('Notifications'),
  icon: 'bell',
  context: EContext.NOTIFICATIONS,
  active: props.currentContext === EContext.NOTIFICATIONS,
  open: props.openContext === EContext.NOTIFICATIONS,
}))
const MENUS_ME = computed<IMenuItem>(() => ({
  // name: $i18n('MyUniverse'), // IR-1060972 tactical fix for missing translation
  name: DS_LANG === 'en' ? $i18n('Me') : $i18n('MySpace'),
  icon: 'user-alt',
  context: EContext.ME,
  active: props.currentContext === EContext.ME,
  open: props.openContext === EContext.ME,
}))
const MENUS_NETWORK = computed<IMenuItem>(() => ({
  badge: storeInvitations.showInvitesBadge ? storeInvitations.countInvites : 0,
  name: $i18n('Network'),
  icon: 'network',
  context: EContext.NETWORK,
  active: props.currentContext === EContext.NETWORK,
  open: props.openContext === EContext.NETWORK,
}))
const MENUS_ADMIN = computed<IMenuItem>(() => ({
  name: $i18n('Admin'),
  icon: '3dswym-cog',
  context: EContext.ADMIN,
  active: props.currentContext === EContext.ADMIN,
  open: props.openContext === EContext.ADMIN,
}))
// TODO : add tracker
// eslint-disable-next-line complexity
async function togglePinned(item: IMenuItem) {
  let label: 'clickOnCommunities'
    | 'clickOnConversations'
    | 'clickOnAURA'
    | 'clickOnRituals'
    | 'clickOnMe'
    | 'clickOnNetwork'
    | 'clickOnAdmin'
    | 'clickOnCalls'
    | 'clickOnAppUpdate'
    | null = null

  if (item.context === EContext.COMMUNITY)
    label = 'clickOnCommunities'
  else if (item.context === EContext.DM)
    label = 'clickOnConversations'
  else if (item.context === EContext.AURA)
    label = 'clickOnAURA'
  else if (item.context === EContext.RITUALS)
    label = 'clickOnRituals'
  else if (item.context === EContext.ME)
    label = 'clickOnMe'
  else if (item.context === EContext.NETWORK)
    label = 'clickOnNetwork'
  else if (item.context === EContext.ADMIN)
    label = 'clickOnAdmin'
  else if (item.context === EContext.CALLS)
    label = 'clickOnCalls'
  else if (item.context === EContext.APPUPDATE)
    label = 'clickOnAppUpdate'
  if (label === 'clickOnCommunities') {
    trackPageEvent({
      category: 'ThreadTabs',
      label: 'showCommunities',
      event: 'click',
    })
  }
  else if (label === 'clickOnConversations') {
    trackPageEvent({
      category: 'ThreadTabs',
      label: 'showConversations',
      event: 'click',
    })
  }
  else if (label === 'clickOnMe') {
    const category = isMobileBreakpoint.value ? 'PlusMenu' : 'NavBar'

    trackPageEvent({
      category,
      label,
      event: 'click',
    })
  }
  else if (label === 'clickOnCalls') {
    storeConversations.resetMissedBadge()

    trackPageEvent({
      category: 'ThreadTabs',
      label: 'showCalls',
      event: 'click',
    })
  }
  if (item.type === 'native' && item.nativeContext) {
    const PlatformAPI = await requirejsOne<PlatformAPI_AMD>('DS/PlatformAPI/PlatformAPI')
    PlatformAPI.publish(item.nativeContext.topic, item.nativeContext.action)
    return
  }
  else if (item.type === 'no-navigation') {
    // do nothing, since this is not a navigation context
    return
  }
  return emit('update:context-pin', item.context)
}

const items = computed(() => {
  // native app options are always in the more, never here
  let menus = [
    MENUS_CONVERSATIONS.value,
    MENUS_COMMUNITIES.value,
    MENUS_AURA.value,
    MENUS_CALLS.value,
    MENUS_RITUALS.value,
    MENUS_ME.value,
    MENUS_NETWORK.value,
    MENUS_ADMIN.value,
  ]
  const except = (a: IMenuItem[], b: IMenuItem[]) => a.filter(menu => !b.includes(menu))

  if (props.compact || !isRitualsActivated.value) {
    // rituals go into more on mobile
    menus = except(menus, [MENUS_RITUALS.value])
  }

  if (props.compact || !isAURAEnabled.value)
    // AURA go into more on mobile
    menus = except(menus, [MENUS_AURA.value])

  if (props.compact || !(hasCalls.value))
    menus = except(menus, [MENUS_CALLS.value])

  if (props.compact || !hasAccessTo(ROUTE_ME.CONTRIBUTIONS_HOME))
    menus = except(menus, [MENUS_ME.value])

  if (props.compact || !hasAccessTo(ROUTE_NETWORK.ACTIVITY))
    menus = except(menus, [MENUS_NETWORK.value])

  if (props.compact || !hasAccessTo(ROUTE_ADMIN.ADMIN_TENANT) || userStore.currentUser?.tenant_admin !== '1')
    menus = except(menus, [MENUS_ADMIN.value])

  return menus
})
/**
 *
 * @param legal - Legal data fetched from W3DLegal
 * @param itemName - options which can be cookies,about,dp, legalInfo
 * @param compassUrl - url
 * @description - this function will built the envInfo according to the item and download the file
 */
function onLegalItemClick(
  legal: LegalOptions,
  itemName: string,
  compassUrl: string,
) {
  const options = legal?.footer?.options
  const footerInfo = options?.legalInfo
  let targetInfo
  let envInfo

  switch (itemName) {
    case 'tou' :

      if (footerInfo.acceptedTou.length > 1)
        return

      targetInfo = footerInfo.acceptedTou[0]
      envInfo = {
        file: targetInfo.tou,
        envid: targetInfo.id,
        type: 'tou',
        ressourceType: targetInfo.env && targetInfo.env.ressourceType,
      }
      break
    case 'dp' :

      if (footerInfo.acceptedDp.length > 1)
        return

      targetInfo = footerInfo.acceptedDp[0]
      envInfo = {
        file: targetInfo.dp,
        envid: targetInfo.id,
        type: 'dp',
        ressourceType: targetInfo.env && targetInfo.env.ressourceType,
      }
      break
    case 'cookies':

      if (footerInfo.acceptedCookies.length > 1)
        return

      targetInfo = footerInfo.acceptedCookies[0]
      envInfo = {
        file: targetInfo.cookie,
        envid: targetInfo.id,
        type: 'cookie',
        ressourceType: targetInfo.env && targetInfo.env.ressourceType,
      }
      break
    case 'about':
      legal.aboutHandler && legal.aboutHandler()
      break
    case 'legalinfo':

      if (footerInfo.legalInfos.length > 1)
        return

      targetInfo = footerInfo.legalInfos[0]
      envInfo = {
        file: targetInfo.legalInfo,
        envid: targetInfo.id,
        type: 'legalinfo',
        ressourceType: targetInfo.env && targetInfo.env.ressourceType,
      }
      break
  }

  if (envInfo && envInfo.type && envInfo.type.toLowerCase() === 'cookie' && !envInfo.file) {
    legal.footer.elements.list.querySelector<HTMLSpanElement>('li[name=cookies] span')
      ?.click()
  }

  else {
    envInfo && getLegalInfoFiles(
      envInfo,
      legal.language,
      compassUrl,
    )
  }
}

/**
 * @description - function will fetch the legal data from W3DLegal service and add legal items in the more menu
 */
async function getLegalInformation() {
  const W3DLegal = await requirejsOne<W3DLegalItem>('DS/W3DLegal/W3DLegal')
  const compassUrl = await getServiceUrl(SERVICE.COMPASS)
  const items = W3DLegal.displayFooter()?.items
  if (!items) {
    W3DLegalItems.value.push({
      text: $i18n('About3DPlatform'),
      handler: () => onLegalItemClick(
        W3DLegal._legal,
        'about',
        compassUrl,
      ),
    })
  }
  else {
    items?.forEach((item) => {
      W3DLegalItems.value.push({
        text: item.text.trim(),
        handler: () => onLegalItemClick(
          W3DLegal._legal,
          item.name,
          compassUrl,
        ),

      })
    })
  }
}

whenever(
  isLegalInfoFetched,
  getLegalInformation,
  { immediate: true },
)

/**
 * @description By default communitites tab open in  admin control center page.
 */
async function openAdminControlCenter() {
  router.push({ name: ROUTE_ADMIN.ADMIN_TENANT })
}

// eslint-disable-next-line max-statements
const menuMoreItems = computed(() => {
  const items: DropdownMenu.Item[] = []

  // HTP1: Whether to display an Update button on sidebar
  // Since the native app may need to use an Update
  // button in case if there's new update available.
  // The Update button will be hidden if there's no update.
  if (storeServiceWorker.useUpdateUI.value) {
    items.push({
      text: $i18n('AppUpdate'),
      fonticon: 'reload',
      selected: false,
      hidden: !storeServiceWorker.showUpdate.value,
      handler() { updateAppClicked() },
    })
  }

  if (isAURAEnabled.value) {
    items.push({
      text: AI_NAME,
      fonticon: 'virtual-companions',
      selected: MENUS_AURA.value.active,
      handler() { togglePinned(MENUS_AURA.value) },
    })
  }
  if (isRitualsActivated.value) {
    items.push({
      text: $i18n('Rituals'),
      fonticon: 'calendar-clock',
      selected: MENUS_RITUALS.value.active,
      handler() { togglePinned(MENUS_RITUALS.value) },
    })
  }
  if (hasAccessTo(ROUTE_ME.CONTRIBUTIONS_HOME)) {
    items.push({
      text: 'Me', // Always use Me in english
      fonticon: 'user-alt',
      selected: MENUS_ME.value.active,
      handler() { togglePinned(MENUS_ME.value) },
    })
  }
  if (hasAccessTo(ROUTE_NETWORK.ACTIVITY)) {
    items.push({
      text: $i18n('Network'),
      fonticon: 'network',
      selected: MENUS_NETWORK.value.active,
      handler() { togglePinned(MENUS_NETWORK.value) },
    })
  }
  if (hasAccessTo(ROUTE_ADMIN.ADMIN_TENANT) && userStore.currentUser?.tenant_admin === '1') {
    items.push({
      text: $i18n('Admin'),
      fonticon: '3dswym-cog',
      selected: MENUS_ADMIN.value.active,
      handler() { openAdminControlCenter() },
    })
  }
  if (W3DLegalItems.value.length > 0) {
    items.push({
      text: $i18n('Help'),
      fonticon: 'question-circle',
      items: W3DLegalItems.value,
      class: 'legal-help',
    })
  }
  if (isLogoutActivated.value) {
    items.push({
      label: $i18n('Logout'),
      fonticon: 'logout',
      handler: () => logout(),
    })
  }

  return items
})
const isMenuMoreActive = computed(() => !!menuMoreItems.value.find(el => el.selected))

function onHover(item: IMenuItem, isHover: boolean) {
  if (useFeatureManager('app.config.touch_device').value || item.type === 'no-navigation')
    return

  emit('update:context-active', isHover ? item.context : EContext.NULL)
}

async function openNotification() {
  trackPageEvent({
    category: 'NavBar',
    label: 'clickOnNotifications',
    event: 'click',
  })
  togglePinned(MENUS_NOTIFICATIONS.value)
  router.push({ name: ROUTE_NOTIFICATION.HOME })
  // const [TopFrame] = await requirejs(['DS/TopFrame/TopFrame'])
  // TopFrame.openPanel('right', { tag: 'notification' })
}
function updateAppClicked() {
  // HTP1: in case of native app, let the app native manages the update reload
  // otherwise, if autoReload is allowed, then use regular updateSW that will reload page
  if (Preferences.isNativeApp() && appProps && appProps.useNativeUpdate) {
    if (storeServiceWorker.allowAutoReload.value)
      useServiceWorkerWrapper.updateSW(true)
    else
      storeServiceWorker.notifyAppToUpdate(storeServiceWorker.nativeUpdateInfo.value)
  }
  else {
    // HTP1: on web browser, reload page to update
    useServiceWorkerWrapper.updateSW(true)
  }
}
const extraItemNumber = computed(() => {
  let num = 1 // "Plus" menu

  if (!isWidget && isMobileBreakpoint.value)
    num++
  if (isMobileBreakpoint.value && hasCalls.value)
    num++

  return num
})
</script>

<template>
  <nav
    class="navbar_container !bg-white"
    :class="{
      'left-layout': layout === 'left',
    }"
    :style="
      layout === 'bottom'
        ? `grid-template-columns: repeat(${items?.length + extraItemNumber}, minmax(0, 1fr));`
        : ''
    "
  >
    <!-- App Update item -->
    <MBlockItem
      v-if="!isMobileBreakpoint && storeServiceWorker.showUpdate.value"
      :active="MENUS_APPUPDATE.active"
      :badge-update="true"
      :icon="MENUS_APPUPDATE.icon"
      :mobile-ui="layout === 'bottom'"
      :open="MENUS_APPUPDATE.open"
      :title="MENUS_APPUPDATE.name"
      @click="updateAppClicked"
    />
    <div
      v-for="menu in items"
      :key="menu.name"
      v-element-hover="onHover.bind(null, menu)"
      class="navbar_items-container"
      @click="togglePinned(menu)"
    >
      <MBlockItem
        :active="menu.active"
        :badge="menu.badge"
        :icon="menu.icon"
        :mobile-ui="layout === 'bottom'"
        :open="menu.open"
        :title="menu.name"
      />
    </div>
    <!-- Notifications item -->
    <MBlockItem
      v-if="!isWidget && isMobileBreakpoint"
      :active="MENUS_NOTIFICATIONS.active"
      :badge="notificationCount"
      :icon="MENUS_NOTIFICATIONS.icon"
      :mobile-ui="layout === 'bottom'"
      :open="MENUS_NOTIFICATIONS.open"
      :title="MENUS_NOTIFICATIONS.name"
      @click="openNotification"
    />
    <!-- Calls icon after Notifications item -->
    <div
      v-if="compact && hasCalls"
      v-element-hover="onHover.bind(null, MENUS_CALLS)"
    >
      <MBlockItem
        :active="MENUS_CALLS.active"
        :badge="MENUS_CALLS.badge"
        :icon="MENUS_CALLS.icon"
        :mobile-ui="layout === 'bottom'"
        :open="MENUS_CALLS.open"
        :title="MENUS_CALLS.name"
        @click="togglePinned(MENUS_CALLS)"
      />
    </div>
    <vu-dropdownmenu
      v-if="compact"
      class="navbar_more-container"
      :items="menuMoreItems.concat(addonMobileItems)"
      position="top-left"
    >
      <MBlockItem
        :active="isMenuMoreActive"
        :badge-update="storeServiceWorker.showUpdate.value"
        :icon="MENUS_MORE.icon"
        :mobile-ui="layout === 'bottom'"
        :title="MENUS_MORE.name"
      />
    </vu-dropdownmenu>
    <template v-else-if="!isMobileBreakpoint && addonMobileItems.length">
      <MBlockItem
        :active="showMore"
        :icon="showMore ? 'chevron-up' : 'chevron-down'"
        :mobile-ui="layout === 'bottom'"
        :title="MENUS_MORE.name"
        @click="toggle()"
      />
      <template v-if="showMore">
        <MBlockItem
          v-for="icon in addonMobileItems"
          :key="`icon-${icon.text}`"
          :icon="icon.fonticon"
          :mobile-ui="layout === 'bottom'"
          :title="icon.text"
          @click="toggle(false); icon.handler()"
        />
      </template>
    </template>
  </nav>
</template>

<style scoped lang="scss">
.navbar_container :deep(.fonticon-bell) {
  display: initial !important;
}

.navbar_container {
  &.left-layout {
    @apply bg-grey-3 z-10 w-full flex h-auto flex-col pt-[50px] justify-start border-r border-grey-4 overflow-y-auto;

    .navbar_items-container { @apply inline-block hover:no-underline no-underline grow-0 cursor-pointer; }
    .navbar_more-container { @apply grow flex justify-center hover:no-underline no-underline md:grow-0 cursor-pointer; }
  }
  &:not(.left-layout) {
    @apply grid pt-0 border-r-0 px-2 border-t border-grey-4;
  }
}
</style>
